import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createDattoAuthConfig,
  DATTO_MAX_PAGE_SIZE,
  deleteDattoAuthConfig,
  disconnectDattoIntegration,
  getDattoAuthConfig,
  getDattoCustomers,
  getDattoDevices,
  getDattoMappedCustomers,
  getDattoMappedDevices,
  mapDattoCustomers,
  mapDattoDevices,
  updateDattoAuthConfig,
  updateDattoStatus,
} from "controller/api";
import { ApiErrorType } from "shared";
import {
  CreateDattoAuthConfigInput,
  MapDattoDevicesInput,
  MappDattoCustomersInput,
  UpdateDattoAuthConfigInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";

export const useCreateDattoAuthConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDattoAuthConfigInput) =>
      createDattoAuthConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dattoAuthConfig"],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "createDattoAuthConfig",
      });
    },
  });
};

export const useUpdateDattoAuthConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateDattoAuthConfigInput) =>
      updateDattoAuthConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dattoAuthConfig"],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "updateDattoAuthConfig",
      });
    },
  });
};

export const useGetDattoAuthConfig = () => {
  return useQuery({
    queryKey: ["dattoAuthConfig"],
    queryFn: () => getDattoAuthConfig(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getDattoAuthConfig",
      });
    },
    // staleTime: Infinity,
  });
};

/**
 * A runaway guard, not a product limit: `hasMore` already terminates paging, so
 * this only bounds a backend that never stops advertising a next page.
 */
const MAX_PAGES = 40;

export const useGetDattoCustomers = (perPage: number = DATTO_MAX_PAGE_SIZE) => {
  return useInfiniteQuery({
    queryKey: ["dattoCustomers", perPage],
    queryFn: ({ pageParam = 0 }) =>
      getDattoCustomers({
        page: pageParam as number,
        perPage,
      }),
    // Driven by Datto's `nextPageUrl`, never by the row count: Datto caps
    // `per_page` server-side, so a page shorter than `perPage` is not the last.
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.hasMore && allPages.length < MAX_PAGES
        ? allPages.length
        : undefined,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getDattoCustomers",
      });
    },
  });
};

export const useGetDattoDevices = (perPage: number = DATTO_MAX_PAGE_SIZE) => {
  return useInfiniteQuery({
    queryKey: ["dattoDevices", perPage],
    queryFn: ({ pageParam = 0 }) =>
      getDattoDevices({ page: pageParam as number, perPage }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.hasMore && allPages.length < MAX_PAGES
        ? allPages.length
        : undefined,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getDattoDevices",
      });
    },
    // staleTime: Infinity,
  });
};

export const useMapDattoCustomers = () => {
  return useMutation({
    mutationFn: (data: MappDattoCustomersInput) => mapDattoCustomers(data),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "mapDattoCustomers",
      });
    },
  });
};

export const useGetDattoMappedCustomers = () => {
  return useQuery({
    queryKey: ["dattoMappedCustomers"],
    queryFn: () => getDattoMappedCustomers(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getDattoMappedCustomers",
      });
    },
  });
};

export const useMapDattoDevices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MapDattoDevicesInput) => mapDattoDevices(data),
    // Without this the screen keeps serving the pre-save mapping list, so a
    // reopened drawer shows stale device → user assignments.
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dattoMappedDevices"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "mapDattoDevices",
      });
    },
  });
};

export const useGetDattoMappedDevices = () => {
  return useQuery({
    queryKey: ["dattoMappedDevices"],
    queryFn: () => getDattoMappedDevices(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getDattoMappedDevices",
      });
    },
  });
};

export const useDeleteDattoAuthConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteDattoAuthConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dattoAuthConfig"],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "deleteDattoAuthConfig",
      });
    },
  });
};

export const useDisconnectDattoIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectDattoIntegration(),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dattoAuthConfig"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "disconnectDattoIntegration",
      });
    },
  });
};

export const useUpdateDattoStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: boolean) => updateDattoStatus(status),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["integrationSources"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "updateDattoStatus",
      });
    },
  });
};
